import { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import Sidebar from '../components/Sidebar';

const API = 'https://affinityconcept.tech/api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Analytics color scheme — platform green, orange for outstanding, blue for received line.
const C_GREEN = '#1D9E75';
const C_ORANGE = '#E8833A';
const C_BLUE = '#3B82F6';

// "YYYY-MM" bucket key from an ISO date, and a short "Mon YY" label from that key.
const monthKey = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (key) => {
  const [y, m] = key.split('-');
  return `${MONTHS[+m - 1]} '${y.slice(2)}`;
};
const monthLabelLong = (key) => {
  const [y, m] = key.split('-');
  return `${MONTHS[+m - 1]} ${y}`;
};

const DEVELOPERS = ['Uniestate', 'Imtiaz', 'Tissolli (OBG)', 'Binghatti', 'ADNM', 'Wadan'];
const UNIT_TYPES = ['Studio', '1 Bed', '2 Beds', '3 Beds', '4 Bed Penthouse', 'Multiples', 'Other'];
const SPA_STATUSES = ['Pending', 'Signed', 'Signed + Oqood'];
const TEAM = ['Noam', 'Eran', 'Alex'];

const PAYMENT_TYPES = [
  { value: 'received', label: 'Received', badge: 'bg-green-100 text-green-700' },
  { value: 'salary_withheld', label: 'Salary Withheld', badge: 'bg-purple-100 text-purple-700' },
  { value: 'pending_developer', label: 'Pending from Developer', badge: 'bg-amber-100 text-amber-700' },
];

const today = () => new Date().toISOString().split('T')[0];

const emptyForm = (deal_type) => ({
  deal_type,
  deal_date: today(),
  client_name: '',
  broker_name: '',
  nationality: '',
  developer: '',
  project_name: '',
  unit_type: 'Studio',
  unit_number: '',
  selling_price: '',
  developer_commission_pct: '',
  total_commission: '',
  // split percentages — broker: 50/50/0, manager (company/manager David/agent): 50/5/45
  company_commission_pct: '50',
  my_commission_pct: deal_type === 'broker' ? '50' : '5',
  referral_commission_pct: deal_type === 'broker' ? '0' : '',
  agent_commission_pct: deal_type === 'manager' ? '45' : '',
  // split amounts (auto-calculated, overridable)
  company_commission_amount: '',
  my_commission: '',
  referral_commission_amount: '',
  agent_commission_amount: '',
  spa_status: 'Pending',
  notes: '',
});

// Fields whose AED value is auto-calculated but manually overridable.
const AMOUNT_FIELDS = ['total_commission', 'company_commission_amount', 'my_commission', 'referral_commission_amount', 'agent_commission_amount'];
const noOverride = () => AMOUNT_FIELDS.reduce((o, k) => ({ ...o, [k]: false }), {});
const strAmt = (v) => (v === null || v === undefined ? '' : String(v));

// Compute derived AED amounts from form + override snapshot.
// total = selling_price * developer_pct/100 ; each split = total * pct/100 (unless overridden).
const derive = (f, ov) => {
  const sp = num(f.selling_price), dp = num(f.developer_commission_pct);
  const autoTotal = sp != null && dp != null ? +(sp * dp / 100).toFixed(2) : null;
  const total = ov.total_commission ? num(f.total_commission) : autoTotal;
  const t = total || 0;
  const amt = (pct, flag, cur) => flag ? num(cur) : (num(pct) != null ? +(t * num(pct) / 100).toFixed(2) : null);
  return {
    total_commission: total,
    company_commission_amount: amt(f.company_commission_pct, ov.company_commission_amount, f.company_commission_amount),
    my_commission: amt(f.my_commission_pct, ov.my_commission, f.my_commission),
    referral_commission_amount: amt(f.referral_commission_pct, ov.referral_commission_amount, f.referral_commission_amount),
    agent_commission_amount: amt(f.agent_commission_pct, ov.agent_commission_amount, f.agent_commission_amount),
  };
};

const emptyPayment = () => ({
  amount: '',
  payment_date: today(),
  payment_type: 'received',
  notes: '',
});

const aed = (n) => 'AED ' + Math.round(n || 0).toLocaleString('en-GB');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-');
const num = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));

// A payment date strictly after today (compared by calendar day).
const isFutureDate = (d) => {
  if (!d) return false;
  const dt = new Date(d); dt.setHours(0, 0, 0, 0);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return dt > t;
};
// Money actually in hand for a deal: received-type + already-passed salary_withheld.
const inHand = (c) => (c.received || 0) + (c.salary_withheld_realized || 0);
// Earliest future-dated salary_withheld date on a deal, formatted (or null).
const nextScheduledDate = (c) => {
  const future = (c.payments || []).filter(p => p.payment_type === 'salary_withheld' && isFutureDate(p.payment_date));
  if (!future.length) return null;
  const earliest = future.reduce((a, b) => (new Date(a.payment_date) < new Date(b.payment_date) ? a : b));
  return fmtDate(earliest.payment_date);
};

export default function Commissions({ token, onLogout }) {
  const [commissions, setCommissions] = useState([]);
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('broker');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm('broker'));
  const [override, setOverride] = useState(noOverride());
  const [expanded, setExpanded] = useState(null);
  const [payForm, setPayForm] = useState(null);      // { commissionId } while adding
  const [payDraft, setPayDraft] = useState(emptyPayment());
  const [editPay, setEditPay] = useState(null);      // payment being edited

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    const r = await axios.get(`${API}/commissions`, { headers });
    setCommissions(r.data);
  };

  useEffect(() => {
    load();
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
  }, []);

  const rows = commissions.filter(c => c.deal_type === tab);

  // --- Summary for the active tab ---
  const sum = (fn) => rows.reduce((s, c) => s + (fn(c) || 0), 0);
  const totalEarned = sum(c => c.my_commission);
  const totalRealized = sum(c => c.realized_paid);          // received + past salary_withheld
  const totalScheduled = sum(c => c.salary_withheld_scheduled);  // future-dated salary_withheld
  const totalPendingDev = sum(c => c.pending_developer);
  const totalOutstanding = sum(c => c.outstanding);

  // --- Commission form ---
  const openForm = () => {
    setEditItem(null);
    setForm(emptyForm(tab));
    setOverride(noOverride());   // fresh deal: everything auto-calculates
    setShowForm(true);
  };

  const handleEdit = (c) => {
    setEditItem(c);
    const dt = c.deal_type;
    setForm({
      deal_type: dt,
      deal_date: c.deal_date ? c.deal_date.split('T')[0] : today(),
      client_name: c.client_name || '',
      broker_name: c.broker_name || '',
      nationality: c.nationality || '',
      developer: c.developer || '',
      project_name: c.project_name || '',
      unit_type: c.unit_type || 'Studio',
      unit_number: c.unit_number || '',
      selling_price: c.selling_price ?? '',
      developer_commission_pct: c.developer_commission_pct ?? '',
      total_commission: c.total_commission ?? '',
      company_commission_pct: c.company_commission_pct ?? '50',
      my_commission_pct: c.my_commission_pct ?? (dt === 'broker' ? '50' : '5'),
      referral_commission_pct: c.referral_commission_pct ?? (dt === 'broker' ? '0' : ''),
      agent_commission_pct: c.agent_commission_pct ?? (dt === 'manager' ? '45' : ''),
      company_commission_amount: c.company_commission_amount ?? '',
      my_commission: c.my_commission ?? '',
      referral_commission_amount: c.referral_commission_amount ?? '',
      agent_commission_amount: c.agent_commission_amount ?? '',
      spa_status: c.spa_status || 'Pending',
      notes: c.notes || '',
    });
    // Preserve any stored amount exactly (treat as manual); recalc button switches to auto.
    setOverride(AMOUNT_FIELDS.reduce((o, k) => ({ ...o, [k]: c[k] !== null && c[k] !== undefined }), {}));
    setShowForm(true);
  };

  // Change a percentage / price input → recompute non-overridden amounts.
  const changeInput = (key, value) => {
    const f = { ...form, [key]: value };
    const d = derive(f, override);
    setForm({ ...f, ...Object.fromEntries(AMOUNT_FIELDS.map(k => [k, strAmt(d[k])])) });
  };

  // User typed directly into an amount field → mark as manual override.
  const changeAmount = (key, value) => {
    setOverride(o => ({ ...o, [key]: true }));
    setForm(f => ({ ...f, [key]: value }));
  };

  // Reset one amount field back to its auto-calculated value.
  const recalcAmount = (key) => {
    const newOv = { ...override, [key]: false };
    const d = derive(form, newOv);
    setOverride(newOv);
    setForm(f => ({ ...f, ...Object.fromEntries(AMOUNT_FIELDS.map(k => [k, strAmt(d[k])])) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      deal_date: form.deal_date || null,
      selling_price: num(form.selling_price),
      developer_commission_pct: num(form.developer_commission_pct),
      total_commission: num(form.total_commission),
      company_commission_pct: num(form.company_commission_pct),
      my_commission_pct: num(form.my_commission_pct),
      referral_commission_pct: num(form.referral_commission_pct),
      agent_commission_pct: num(form.agent_commission_pct),
      company_commission_amount: num(form.company_commission_amount),
      my_commission: num(form.my_commission),
      referral_commission_amount: num(form.referral_commission_amount),
      agent_commission_amount: num(form.agent_commission_amount),
    };
    if (editItem) await axios.put(`${API}/commissions/${editItem.id}`, payload, { headers });
    else await axios.post(`${API}/commissions`, payload, { headers });
    setShowForm(false);
    setEditItem(null);
    await load();
  };

  // --- Split percentage validation (company + my + referral|agent = 100) ---
  const splitField = form.deal_type === 'broker' ? 'referral' : 'agent';
  const pctSum = (num(form.company_commission_pct) || 0) + (num(form.my_commission_pct) || 0) + (num(form[`${splitField}_commission_pct`]) || 0);
  const pctValid = Math.abs(pctSum - 100) < 0.01;

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this deal and all its payments?')) return;
    await axios.delete(`${API}/commissions/${id}`, { headers });
    await load();
  };

  // --- Payments ---
  const startAddPayment = (c) => {
    setExpanded(c.id);
    setPayForm(c.id);
    setEditPay(null);
    setPayDraft(emptyPayment());
  };

  const savePayment = async (commissionId) => {
    const payload = {
      amount: num(payDraft.amount),
      payment_date: payDraft.payment_date || null,
      payment_type: payDraft.payment_type,
      notes: payDraft.notes || null,
    };
    if (!payload.amount) { window.alert('Enter an amount'); return; }
    if (editPay) await axios.put(`${API}/commissions/payments/${editPay.id}`, payload, { headers });
    else await axios.post(`${API}/commissions/${commissionId}/payments`, payload, { headers });
    setPayForm(null);
    setEditPay(null);
    await load();
  };

  const startEditPayment = (c, p) => {
    setExpanded(c.id);
    setPayForm(c.id);
    setEditPay(p);
    setPayDraft({
      amount: p.amount ?? '',
      payment_date: p.payment_date ? p.payment_date.split('T')[0] : today(),
      payment_type: p.payment_type || 'received',
      notes: p.notes || '',
    });
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    await axios.delete(`${API}/commissions/payments/${id}`, { headers });
    await load();
  };

  const rowColor = (c) => {
    const paid = c.realized_paid || 0;         // received + already-passed salary_withheld
    const target = c.my_commission || 0;
    if (target > 0 && paid >= target - 0.5) return 'bg-green-50';            // fully realized
    // Any realized money, future scheduled salary, or pending-from-developer → in progress
    if (paid > 0 || (c.pending_developer || 0) > 0 || (c.salary_withheld_scheduled || 0) > 0) return 'bg-orange-50';
    return 'bg-red-50';                                                      // nothing yet
  };

  const badgeFor = (type) => PAYMENT_TYPES.find(t => t.value === type) || PAYMENT_TYPES[0];

  const cards = [
    { label: 'Total Commission Earned', value: totalEarned, color: 'text-gray-900' },
    { label: 'Received (in hand)', value: totalRealized, color: 'text-green-600' },
    { label: 'Scheduled (future salary)', value: totalScheduled, color: 'text-blue-600' },
    { label: 'Pending from Developer', value: totalPendingDev, color: 'text-orange-600' },
    { label: 'Outstanding', value: totalOutstanding, color: 'text-red-600' },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">💼 Commissions</h1>
            <p className="text-gray-500 text-sm">Skyhigh Dubai — real estate deal commissions</p>
          </div>
          <button onClick={openForm}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
            + Add Deal
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'broker', label: 'My Deals' },
            { key: 'manager', label: 'Managed Deals' },
          ].map(t => (
            <button key={t.key}
              onClick={() => { setTab(t.key); setShowForm(false); setExpanded(null); }}
              className={`px-5 py-2 rounded-xl text-sm font-medium transition ${
                tab === t.key ? 'bg-green-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}>
              {t.label} <span className={`ml-1 ${tab === t.key ? 'text-green-100' : 'text-gray-400'}`}>
                ({commissions.filter(c => c.deal_type === t.key).length})
              </span>
            </button>
          ))}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {cards.map(c => (
            <div key={c.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-xs text-gray-500 mb-1">{c.label}</p>
              <p className={`text-lg font-bold ${c.color}`}>{aed(c.value)}</p>
            </div>
          ))}
        </div>

        {/* Add/Edit form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h2 className="text-base font-semibold mb-4">
              {editItem ? 'Edit Deal' : `New ${tab === 'broker' ? 'My Deal' : 'Managed Deal'}`}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Deal Date">
                <input type="date" value={form.deal_date}
                  onChange={e => setForm({ ...form, deal_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Client Name">
                <input value={form.client_name} placeholder="Client name"
                  onChange={e => setForm({ ...form, client_name: e.target.value })} className={inputCls} />
              </Field>

              {tab === 'manager' && (
                <Field label="Broker (team member)">
                  <input list="team-list" value={form.broker_name} placeholder="Noam / Eran / Alex"
                    onChange={e => setForm({ ...form, broker_name: e.target.value })} className={inputCls} />
                  <datalist id="team-list">{TEAM.map(t => <option key={t} value={t} />)}</datalist>
                </Field>
              )}
              <Field label="Nationality">
                <input value={form.nationality} placeholder="Nationality"
                  onChange={e => setForm({ ...form, nationality: e.target.value })} className={inputCls} />
              </Field>

              <Field label="Developer">
                <input list="dev-list" value={form.developer} placeholder="Developer"
                  onChange={e => setForm({ ...form, developer: e.target.value })} className={inputCls} />
                <datalist id="dev-list">{DEVELOPERS.map(d => <option key={d} value={d} />)}</datalist>
              </Field>
              <Field label="Project">
                <input value={form.project_name} placeholder="Project name"
                  onChange={e => setForm({ ...form, project_name: e.target.value })} className={inputCls} />
              </Field>

              <Field label="Unit Type">
                <select value={form.unit_type} onChange={e => setForm({ ...form, unit_type: e.target.value })} className={inputCls}>
                  {UNIT_TYPES.map(u => <option key={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="Unit Number">
                <input value={form.unit_number} placeholder="e.g. 710"
                  onChange={e => setForm({ ...form, unit_number: e.target.value })} className={inputCls} />
              </Field>

              <Field label="Selling Price (AED)">
                <input type="number" step="any" value={form.selling_price} placeholder="0"
                  onChange={e => changeInput('selling_price', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Commission from Developer %">
                <input type="number" step="any" value={form.developer_commission_pct} placeholder="e.g. 5"
                  onChange={e => changeInput('developer_commission_pct', e.target.value)} className={inputCls} />
              </Field>

              {/* --- Commission split calculator --- */}
              <div className="md:col-span-2 bg-gray-50 rounded-xl border border-gray-200 p-4 mt-1">
                {/* Prominent total commission */}
                <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Total Commission (AED)</label>
                    <div className="flex items-center gap-1">
                      <input type="number" step="any" value={form.total_commission} placeholder="0"
                        onChange={e => changeAmount('total_commission', e.target.value)}
                        className="w-44 border border-gray-300 rounded-xl px-3 py-2 text-lg font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500" />
                      {override.total_commission && (
                        <button type="button" onClick={() => recalcAmount('total_commission')} title="Recalculate from selling price × developer %"
                          className="text-gray-400 hover:text-green-600 text-lg px-1">↺</button>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {override.total_commission ? 'Manual override' : 'Auto = selling price × developer %'}
                    </p>
                  </div>
                  {/* Validation indicator */}
                  <div className={`text-xs px-3 py-2 rounded-lg font-medium ${pctValid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    Split total: {(+pctSum.toFixed(2))}% {pctValid ? '✓' : '⚠ should equal 100%'}
                  </div>
                </div>

                {/* Split rows */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <SplitRow label="Company" pctKey="company_commission_pct" amtKey="company_commission_amount"
                    form={form} override={override} onPct={changeInput} onAmount={changeAmount} onRecalc={recalcAmount} />
                  <SplitRow label={form.deal_type === 'broker' ? 'My Commission' : 'Manager (David)'} pctKey="my_commission_pct" amtKey="my_commission"
                    form={form} override={override} onPct={changeInput} onAmount={changeAmount} onRecalc={recalcAmount} highlight />
                  {form.deal_type === 'broker' ? (
                    <SplitRow label="Referral" pctKey="referral_commission_pct" amtKey="referral_commission_amount"
                      form={form} override={override} onPct={changeInput} onAmount={changeAmount} onRecalc={recalcAmount} />
                  ) : (
                    <SplitRow label="Agent (broker)" pctKey="agent_commission_pct" amtKey="agent_commission_amount"
                      form={form} override={override} onPct={changeInput} onAmount={changeAmount} onRecalc={recalcAmount} />
                  )}
                </div>
              </div>

              <Field label="SPA Status">
                <select value={form.spa_status} onChange={e => setForm({ ...form, spa_status: e.target.value })} className={inputCls}>
                  {SPA_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Notes">
                <input value={form.notes} placeholder="Notes (optional)"
                  onChange={e => setForm({ ...form, notes: e.target.value })} className={inputCls} />
              </Field>

              <div className="md:col-span-2 flex gap-3 mt-1">
                <button type="submit" className="bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-medium">
                  {editItem ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditItem(null); }}
                  className="border border-gray-200 px-5 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Commissions table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="p-3 w-8"></th>
                  {['Date', 'Client', 'Developer', 'Project', 'Unit'].map(h => (
                    <th key={h} className="text-left p-3 text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                  {['Selling Price', 'My Commission', 'Paid', 'Outstanding'].map(h => (
                    <th key={h} className="text-right p-3 text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">SPA</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(c => (
                  <Fragment key={c.id}>
                    <tr onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      className={`border-b border-gray-50 cursor-pointer hover:brightness-[0.97] transition ${rowColor(c)}`}>
                      <td className="p-3 text-center">
                        <button onClick={(e) => { e.stopPropagation(); setExpanded(expanded === c.id ? null : c.id); }}
                          className="text-gray-400 hover:text-gray-700">
                          {expanded === c.id ? '▾' : '▸'}
                        </button>
                      </td>
                      <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{fmtDate(c.deal_date)}</td>
                      <td className="p-3 text-sm font-medium text-gray-900">
                        {c.client_name || '-'}
                        {c.deal_type === 'manager' && c.broker_name && (
                          <span className="block text-xs text-gray-400">via {c.broker_name}</span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-gray-700">{c.developer || '-'}</td>
                      <td className="p-3 text-sm text-gray-700">{c.project_name || '-'}</td>
                      <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                        {c.unit_type}{c.unit_number ? ` · ${c.unit_number}` : ''}
                      </td>
                      <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{aed(c.selling_price)}</td>
                      <td className="p-3 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">{aed(c.my_commission)}</td>
                      <td className="p-3 text-sm text-green-600 text-right whitespace-nowrap">{aed(c.total_paid)}</td>
                      <td className="p-3 text-sm font-semibold text-red-600 text-right whitespace-nowrap">{aed(c.outstanding)}</td>
                      <td className="p-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">{c.spa_status || 'Pending'}</span>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button onClick={(e) => { e.stopPropagation(); startAddPayment(c); }}
                          title="Record a payment for this deal"
                          className="bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded-lg text-xs font-medium mr-2">
                          💰 Payments{c.payments.length ? ` (${c.payments.length})` : ''}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleEdit(c); }} className="text-gray-400 hover:text-blue-500 text-xs mr-2">✏️</button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }} className="text-gray-300 hover:text-red-500 text-xs">✕</button>
                      </td>
                    </tr>

                    {expanded === c.id && (
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <td colSpan={12} className="p-4">
                          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                              <span className="text-gray-500">My Commission <span className="font-semibold text-gray-900">{aed(c.my_commission)}</span></span>
                              <span className="text-gray-500">Received <span className="font-semibold text-green-700">{aed(inHand(c))}</span></span>
                              {c.salary_withheld_scheduled > 0 && (
                                <span className="text-gray-500">Scheduled <span className="font-semibold text-blue-600">{aed(c.salary_withheld_scheduled)}</span>
                                  {nextScheduledDate(c) && <span className="text-xs text-blue-500"> · {nextScheduledDate(c)}</span>}
                                </span>
                              )}
                              {c.pending_developer > 0 && <span className="text-gray-500">Pending from Developer <span className="font-semibold text-orange-600">{aed(c.pending_developer)}</span></span>}
                              <span className="text-gray-500">Outstanding <span className={`font-semibold ${c.outstanding > 0.5 ? 'text-red-600' : 'text-gray-400'}`}>{aed(c.outstanding)}</span></span>
                            </div>
                            <button onClick={() => startAddPayment(c)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                              + Add Payment
                            </button>
                          </div>

                          {/* Commission split breakdown */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                            <SplitStat label="Total Commission" pct={c.developer_commission_pct} pctLabel="from developer" amount={c.total_commission} />
                            <SplitStat label="Company" pct={c.company_commission_pct} amount={c.company_commission_amount} />
                            <SplitStat label={c.deal_type === 'broker' ? 'My Commission' : 'Manager (David)'} pct={c.my_commission_pct} amount={c.my_commission} highlight />
                            {c.deal_type === 'broker'
                              ? <SplitStat label="Referral" pct={c.referral_commission_pct} amount={c.referral_commission_amount} />
                              : <SplitStat label={`Agent${c.broker_name ? ` (${c.broker_name})` : ''}`} pct={c.agent_commission_pct} amount={c.agent_commission_amount} />}
                          </div>

                          {/* Progress bar */}
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
                            <div className="h-full bg-green-500"
                              style={{ width: `${c.my_commission ? Math.min(100, (c.total_paid / c.my_commission) * 100) : 0}%` }} />
                          </div>

                          {/* Payment form */}
                          {payForm === c.id && (
                            <div className="bg-white rounded-xl border border-gray-200 p-3 mb-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">Amount (AED)</label>
                                <input type="number" step="any" value={payDraft.amount}
                                  onChange={e => setPayDraft({ ...payDraft, amount: e.target.value })} className={inputCls} />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">Date</label>
                                <input type="date" value={payDraft.payment_date}
                                  onChange={e => setPayDraft({ ...payDraft, payment_date: e.target.value })} className={inputCls} />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">Type</label>
                                <select value={payDraft.payment_type}
                                  onChange={e => setPayDraft({ ...payDraft, payment_type: e.target.value })} className={inputCls}>
                                  {PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 block mb-1">Notes</label>
                                <input value={payDraft.notes} placeholder="Optional"
                                  onChange={e => setPayDraft({ ...payDraft, notes: e.target.value })} className={inputCls} />
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => savePayment(c.id)}
                                  className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs font-medium">
                                  {editPay ? 'Update' : 'Save'}
                                </button>
                                <button onClick={() => { setPayForm(null); setEditPay(null); }}
                                  className="border border-gray-200 px-3 py-2 rounded-lg text-xs">Cancel</button>
                              </div>
                            </div>
                          )}

                          {/* Payment history */}
                          {c.payments.length === 0 ? (
                            <p className="text-xs text-gray-400">No payments recorded yet.</p>
                          ) : (
                            <div className="space-y-1">
                              {c.payments.map(p => {
                                const b = badgeFor(p.payment_type);
                                return (
                                  <div key={p.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-100 px-3 py-2">
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm text-gray-500 w-20">{fmtDate(p.payment_date)}</span>
                                      <span className="text-sm font-semibold text-gray-900">{aed(p.amount)}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${b.badge}`}>{b.label}</span>
                                      {p.payment_type === 'salary_withheld' && isFutureDate(p.payment_date) && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">📅 scheduled for {fmtDate(p.payment_date)}</span>
                                      )}
                                      {p.notes && <span className="text-xs text-gray-400">{p.notes}</span>}
                                    </div>
                                    <div className="flex gap-2">
                                      <button onClick={() => startEditPayment(c, p)} className="text-gray-400 hover:text-blue-500 text-xs">✏️</button>
                                      <button onClick={() => deletePayment(p.id)} className="text-gray-300 hover:text-red-500 text-xs">✕</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <div className="p-12 text-center text-gray-400 text-sm">
              No {tab === 'broker' ? 'own' : 'managed'} deals yet. Click <span className="font-medium">+ Add Deal</span> to get started!
            </div>
          )}
        </div>

        {/* Analytics */}
        <CommissionAnalytics commissions={commissions} activeTab={tab} />
      </main>
    </div>
  );
}

const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500';

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

// Read-only split figure shown in the expanded row breakdown.
function SplitStat({ label, pct, pctLabel, amount, highlight }) {
  const pctText = pct !== null && pct !== undefined ? `${+(+pct).toFixed(2)}%${pctLabel ? ` ${pctLabel}` : ''}` : '';
  return (
    <div className={`rounded-lg border px-3 py-2 ${highlight ? 'border-green-300 bg-green-50' : 'border-gray-100 bg-white'}`}>
      <p className="text-[11px] text-gray-500">{label}{pctText ? ` · ${pctText}` : ''}</p>
      <p className={`text-sm font-semibold ${highlight ? 'text-green-700' : 'text-gray-900'}`}>
        {amount !== null && amount !== undefined ? aed(amount) : '—'}
      </p>
    </div>
  );
}

// One split line: percentage input + live AED amount (overridable with recalc).
function SplitRow({ label, pctKey, amtKey, form, override, onPct, onAmount, onRecalc, highlight }) {
  const overridden = override[amtKey];
  return (
    <div className={`rounded-xl border p-3 ${highlight ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-medium ${highlight ? 'text-green-700' : 'text-gray-600'}`}>{label}</span>
        <span className="text-[11px] text-gray-400">{overridden ? 'manual' : 'auto'}</span>
      </div>
      <div className="flex items-center gap-1 mb-2">
        <input type="number" step="any" value={form[pctKey]} placeholder="0"
          onChange={e => onPct(pctKey, e.target.value)}
          className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        <span className="text-xs text-gray-400">%</span>
      </div>
      <div className="flex items-center gap-1">
        <input type="number" step="any" value={form[amtKey]} placeholder="0"
          onChange={e => onAmount(amtKey, e.target.value)}
          className={`w-full border rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${highlight ? 'border-green-300 font-semibold' : 'border-gray-200'}`} />
        {overridden && (
          <button type="button" onClick={() => onRecalc(amtKey)} title="Recalculate from %"
            className="text-gray-400 hover:text-green-600 text-sm px-1">↺</button>
        )}
      </div>
      <p className="text-[10px] text-gray-400 mt-1">AED</p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Commission Analytics — charts & insights below the deals table.
// ----------------------------------------------------------------------------

const SCOPES = [
  { key: 'broker', label: 'My Deals' },
  { key: 'manager', label: 'Managed' },
  { key: 'all', label: 'All' },
];

function CommissionAnalytics({ commissions, activeTab }) {
  // Default the analytics scope to whatever tab is active (fall back to All).
  const [scope, setScope] = useState(activeTab || 'all');

  const data = scope === 'all' ? commissions : commissions.filter(c => c.deal_type === scope);

  // --- Monthly aggregation (by deal_date): received vs outstanding ---
  const monthMap = {};
  data.forEach(c => {
    const k = monthKey(c.deal_date);
    if (!k) return;
    if (!monthMap[k]) monthMap[k] = { key: k, earned: 0, received: 0, outstanding: 0 };
    const earned = c.my_commission || 0;
    // "Received" = money actually in hand: received + already-passed salary_withheld.
    // Future-dated salary_withheld and pending_developer stay in outstanding.
    const paid = inHand(c);
    monthMap[k].earned += earned;
    monthMap[k].received += paid;
    monthMap[k].outstanding += Math.max(0, earned - paid);
  });
  const monthly = Object.values(monthMap)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map(m => ({ ...m, label: monthLabel(m.key) }));

  // --- Cumulative earned vs received over time ---
  let cumEarned = 0, cumReceived = 0;
  const cumulative = monthly.map(m => {
    cumEarned += m.earned;
    cumReceived += m.received;
    return { label: m.label, earned: cumEarned, received: cumReceived };
  });

  // --- Summary stats ---
  const totalEarned = data.reduce((s, c) => s + (c.my_commission || 0), 0);
  // Received = money actually in hand (received + already-passed salary_withheld).
  const totalReceived = data.reduce((s, c) => s + inHand(c), 0);
  const totalOutstanding = totalEarned - totalReceived;
  const numDeals = data.length;
  const avgCommission = numDeals ? totalEarned / numDeals : 0;
  const bestMonth = monthly.reduce((best, m) => (m.earned > (best ? best.earned : -1) ? m : best), null);

  // --- Developer breakdown (top 5 by total commission earned) ---
  const devMap = {};
  data.forEach(c => {
    const d = (c.developer && c.developer.trim()) || 'Unknown';
    devMap[d] = (devMap[d] || 0) + (c.my_commission || 0);
  });
  const devArr = Object.entries(devMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  const topDev = devArr[0];
  const top5Devs = devArr.slice(0, 5);

  const stats = [
    { label: 'Total Earned', value: aed(totalEarned), color: 'text-gray-900', sub: 'all my commission' },
    { label: 'Total Received', value: aed(totalReceived), color: 'text-green-600', sub: 'in hand (incl. past salary)' },
    { label: 'Outstanding', value: aed(totalOutstanding), color: 'text-orange-600', sub: 'earned − received' },
    { label: 'Number of Deals', value: String(numDeals), color: 'text-gray-900' },
    { label: 'Avg / Deal', value: aed(avgCommission), color: 'text-gray-900' },
    { label: 'Best Month', value: bestMonth ? monthLabelLong(bestMonth.key) : '—', color: 'text-gray-900', sub: bestMonth ? aed(bestMonth.earned) : undefined },
    { label: 'Top Developer', value: topDev ? topDev.name : '—', color: 'text-gray-900', sub: topDev ? aed(topDev.value) : undefined },
  ];

  const hasData = numDeals > 0 && monthly.length > 0;

  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">📊 Commission Analytics</h2>
          <p className="text-gray-500 text-xs">
            Showing <span className="font-medium">{SCOPES.find(s => s.key === scope)?.label}</span> data
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {SCOPES.map(s => (
            <button key={s.key} onClick={() => setScope(s.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                scope === s.key ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {!hasData ? (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center text-gray-400 text-sm">
          No commission data to analyze for this selection yet.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Summary stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {stats.map(s => (
              <div key={s.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                <p className={`text-base font-bold leading-tight ${s.color}`}>{s.value}</p>
                {s.sub && <p className="text-[11px] text-gray-400 mt-0.5">{s.sub}</p>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Section 1 — Commission by Month (stacked) */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Commission by Month</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={compactAed} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip formatter={(v, n) => [aed(v), n]} labelStyle={{ color: '#111827', fontWeight: 600 }}
                    contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="received" stackId="a" name="Received" fill={C_GREEN} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="outstanding" stackId="a" name="Outstanding" fill={C_ORANGE} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Section 2 — Cumulative Earnings */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Cumulative Earnings</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={cumulative} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={compactAed} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip formatter={(v, n) => [aed(v), n]} labelStyle={{ color: '#111827', fontWeight: 600 }}
                    contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="earned" name="Total Earned" stroke={C_GREEN} strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="received" name="Total Received" stroke={C_BLUE} strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-gray-400 mt-2">The gap between the lines is the outstanding amount.</p>
            </div>
          </div>

          {/* Section 4 — Top developers */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Top Developers by Commission</h3>
            <ResponsiveContainer width="100%" height={Math.max(140, top5Devs.length * 44)}>
              <BarChart data={top5Devs} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tickFormatter={compactAed} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} width={110} />
                <Tooltip formatter={(v) => [aed(v), 'Commission']} labelStyle={{ color: '#111827', fontWeight: 600 }}
                  contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} cursor={{ fill: '#f9fafb' }} />
                <Bar dataKey="value" name="Commission" fill={C_GREEN} radius={[0, 4, 4, 0]} barSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact AED axis label, e.g. 2.2M / 510K.
function compactAed(n) {
  const v = Math.abs(n || 0);
  if (v >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${Math.round(n / 1e3)}K`;
  return String(Math.round(n || 0));
}
